import { FC, useCallback } from 'react';
import {
  ObjectType,
  Spinner,
  useObjectLockQuery,
  useRoleQuery,
  checkLockOnPage,
  useIsAdmin,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import { RoleForm } from './RoleForm';

export const RoleEdit = () => {
  const roleId = useNumericParam('id');

  if (!useIsAdmin()) {
    return <Forbidden />;
  }

  if (!roleId) {
    return <NotFound />;
  }

  return <RoleEditInner roleId={roleId} />;
};

const RoleEditInner: FC<{ roleId: number }> = ({ roleId }) => {
  const [{ segment = 'permissions' }] = useQueryParams();
  const {
    data: role,
    isLoading,
    refetch,
  } = useRoleQuery({
    id: roleId,
  });
  const { data: lockData } = useObjectLockQuery({
    objectId: roleId,
    objectType: ObjectType.Role,
  });
  const navigateToRoleView = useNavigateTo(path.roleDetails, {
    id: roleId,
  });

  const navigateToRoleViewSegment = useCallback(
    () => navigateToRoleView({ segment }),
    [navigateToRoleView, segment],
  );
  checkLockOnPage(lockData, navigateToRoleViewSegment);

  const handleCancel = useCallback(() => {
    navigateToRoleViewSegment();
  }, [navigateToRoleViewSegment]);
  const handleSuccess = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!role) {
    return null;
  }

  return (
    <RoleForm
      key={role.modifiedAt}
      mode="edit"
      id={roleId}
      defaultValues={role}
      onClickCancel={handleCancel}
      onSuccess={handleSuccess}
    />
  );
};
